import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import { formatPhone } from "@/UI/inputs/PhoneInput/formatPhone";
import PhoneInput from "@/UI/inputs/PhoneInput";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles.css";

type TyLoginResponse = TyApiResponse<{ operationId: string; isNew: boolean }>;

/** The marketing site hosts both policies, on the apex of the same domain the menus live under. */
const SITE = `https://${constants.menuDomain}`;

export default function LoginScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const token = useAuth((state) => state.token);
    const isAddingAccount = useAuth((state) => state.isAddingAccount);

    const [phone, setPhone] = useState<string | undefined>(undefined);
    /* Asked on every sign-in rather than once at registration: the terms change as the product does,
       and a box ticked months ago says nothing about the version in force today. Unchecked by
       default — a pre-ticked box is not consent. */
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [readPrivacy, setReadPrivacy] = useState(false);

    const hasConsented = acceptedTerms && readPrivacy;

    const { data, loading, error, invoke } = useFetch<TyLoginResponse>("auth/login", { method: "POST" }, false);

    // Signing in is only reachable from an empty device or from an explicit "add another account",
    // so nobody is pushed through an OTP just because they wandered onto /login.
    useEffect(() => {
        if (token && !isAddingAccount) navigate("/select-store", { replace: true });
    }, [token, isAddingAccount, navigate]);

    useEffect(() => {
        if (data?.success) navigate("/verify-otp", { state: { operationId: data.data.operationId, phone, isNew: data.data.isNew } });
    }, [data]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const formatted = formatPhone(phone);
        if (!formatted || !hasConsented) return;
        invoke({ data: { phone: formatted } });
    };

    return (
        <div className="login-screen">
            <form className="login-card" onSubmit={handleSubmit}>
                <img src="/logo-mini-round.svg" className="login-logo" alt="Menuket" />
                <div className="login-header">
                    <p className="login-title">{t.login.title}</p>
                    <p className="login-subtitle">{t.login.subtitle}</p>
                </div>
                <PhoneInput value={phone} onChange={setPhone} placeholder={t.login.phonePlaceholder} disabled={loading} />

                <div className="login-consent">
                    {/* The label carries the toggle, so the text is part of the hit area — these are
                        small targets on a phone. The link inside stops the click from reaching it,
                        otherwise opening the policy would also tick the box. */}
                    <label className="login-consent-row">
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={acceptedTerms}
                            className={`login-checkbox${acceptedTerms ? " checked" : ""}`}
                            onClick={() => setAcceptedTerms((on) => !on)}
                            disabled={loading}
                        />
                        <span className="login-consent-text">
                            {t.login.acceptTermsBefore}{" "}
                            <a href={`${SITE}/terms`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {t.login.acceptTermsLink}
                            </a>
                        </span>
                    </label>

                    <label className="login-consent-row">
                        <button
                            type="button"
                            role="checkbox"
                            aria-checked={readPrivacy}
                            className={`login-checkbox${readPrivacy ? " checked" : ""}`}
                            onClick={() => setReadPrivacy((on) => !on)}
                            disabled={loading}
                        />
                        <span className="login-consent-text">
                            {t.login.readPrivacyBefore}{" "}
                            <a href={`${SITE}/privacy`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                {t.login.readPrivacyLink}
                            </a>
                        </span>
                    </label>
                </div>

                {error && <p className="login-error">{error}</p>}
                <button type="submit" className="login-submit" disabled={!phone || !hasConsented || loading}>
                    {t.login.submitButton}
                </button>
                {/* Only once they have typed a number, so an untouched form is not scolding anyone. */}
                {phone && !hasConsented && <p className="login-consent-hint">{t.login.consentRequired}</p>}
            </form>
        </div>
    );
}
